import type { ReactElement } from 'react';

/**
 * What a group is drawn as (2026-11-05 founder: 题头图和小组分组图都具体一点 — the round-4 mock, Porch). A group carries
 * an `art` key (ember_peer_groups.art, set by hand when the group is opened); the drawing under that key is its cover
 * and its icon everywhere it appears. A group with no key of its own wears the plain tinted ground and the mark of a
 * few people, as every group did before — `groupTint` and `GroupGlyph` in group-parts.
 *
 * A cover is drawn for 390×128 and sliced to whatever the page gives it — but the group's name and icon sit on white
 * that rides 32px up over it (`.gr-id`), so everything the drawing means to show stays above y=90. An icon is drawn
 * for a square and is read at 46px in the list and 64px on the group's head, so it carries no detail smaller than that.
 */

/** One group's drawing: the ground its cover sits on, the cover, and the icon. */
interface GroupArt {
  /** The cover's own ground, behind the drawing (the page's head and, faintly, the pages under it). */
  ground: string;
  cover: ReactElement;
  icon: ReactElement;
}

/**
 * A porch at the end of the day: two chairs, a plant, the light left on by a door standing open a crack — for the
 * parents of a grown child who pulled away (group af6d7269, 2026-11-05).
 */
const PORCH: GroupArt = {
  ground: '#f3e6d3',
  cover: (
    <svg viewBox="0 0 390 128" preserveAspectRatio="xMidYMid slice" aria-hidden>
      <rect width="390" height="128" fill="#f3e6d3" />
      <rect x="0" y="0" width="390" height="44" fill="#e3ece0" />
      <circle cx="36" cy="14" r="20" fill="#f1f6ef" />
      <circle cx="66" cy="22" r="13" fill="#f1f6ef" />
      <rect x="240" y="0" width="150" height="84" fill="#f0e3cf" />
      <rect x="240" y="0" width="150" height="8" fill="#c9a97f" />
      <rect x="240" y="0" width="5" height="84" fill="#ddcdb2" />
      <rect x="290" y="12" width="84" height="72" rx="2" fill="#a97a4a" />
      <rect x="296" y="18" width="72" height="66" fill="#c58d53" />
      <rect x="304" y="25" width="25" height="22" rx="2" fill="#b57c44" />
      <rect x="336" y="25" width="25" height="22" rx="2" fill="#b57c44" />
      <rect x="304" y="53" width="25" height="24" rx="2" fill="#b57c44" />
      <rect x="336" y="53" width="25" height="24" rx="2" fill="#b57c44" />
      <rect x="296" y="18" width="15" height="66" fill="#ffd89b" />
      <circle cx="322" cy="51" r="3" fill="#f6e3c0" />
      <circle cx="271" cy="30" r="19" fill="#ffd89b" fillOpacity="0.42" />
      <rect x="270" y="8" width="2.2" height="9" fill="#8a7054" />
      <path d="M263 17h15l5.5 14h-26z" fill="#f3c173" />
      <path d="M263 17h15l1.8 4.8h-18.6z" fill="#ffe3b2" />
      <rect x="0" y="84" width="390" height="44" fill="#d8b893" />
      <path d="M0 84h390M0 96h390M0 108h390" stroke="#c4a075" strokeWidth="1.4" />
      <g fill="#8a5a33">
        <rect x="48" y="22" width="42" height="6.5" rx="3" />
        <rect x="56" y="27" width="5" height="36" rx="2.2" />
        <rect x="70" y="27" width="5" height="36" rx="2.2" />
        <rect x="84" y="22" width="7.5" height="42" rx="2.8" />
        <rect x="42" y="62" width="50" height="6.5" rx="2.4" />
        <rect x="45" y="68" width="6.5" height="20" rx="2.6" />
        <rect x="83" y="68" width="6.5" height="20" rx="2.6" />
      </g>
      <g fill="#9a6a3f">
        <rect x="136" y="32" width="38" height="6" rx="3" />
        <rect x="143" y="36" width="4.5" height="31" rx="2" />
        <rect x="155" y="36" width="4.5" height="31" rx="2" />
        <rect x="168" y="32" width="7" height="36" rx="2.6" />
        <rect x="131" y="65" width="45" height="6" rx="2.2" />
        <rect x="134" y="71" width="6" height="17" rx="2.5" />
        <rect x="167" y="71" width="6" height="17" rx="2.5" />
      </g>
      <rect x="200" y="66" width="28" height="21" rx="3" fill="#c98a52" />
      <path d="M214 66c-8-3-12-10-11-18 8 1 12 7 11 18zM214 66c7-4 10-11 8-19-7 2-10 9-8 19z" fill="#5d8a5e" />
      <path d="M214 66c-5-6-7-13-3-20 5 5 7 12 3 20z" fill="#6f9d6c" />
    </svg>
  ),
  // The porch light alone: the one thing in the drawing that reads at 46px.
  icon: (
    <svg viewBox="0 0 48 48" aria-hidden>
      <rect width="48" height="48" fill="#a9733c" />
      <path d="M24 25L7 48h34z" fill="#ffe3b2" fillOpacity="0.55" />
      <rect x="22.9" y="4" width="2.2" height="8" rx="1.1" fill="#f0e1c6" />
      <path d="M16 12h16l5.5 13H10.5z" fill="#f8efdc" />
      <path d="M16 12h16l1.7 4.2H14.3z" fill="#fffaf0" />
      <rect x="10" y="24" width="28" height="3" rx="1.5" fill="#e8d2a6" />
      <circle cx="24" cy="29" r="3.6" fill="#fff6e0" />
    </svg>
  ),
};

const ART: Record<string, GroupArt> = { porch: PORCH };

/** The drawing a group wears, or null for the plain tinted ground. */
export function groupArt(art: string | null | undefined): GroupArt | null {
  return (art && ART[art]) || null;
}
